import React, { useCallback, useEffect } from 'react';
import {
  Form, Input, Modal, DatePicker, InputNumber, Radio,
} from 'antd';
import moment from 'moment';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import { selectEditState, selectSubmitPackageRequest, selectFetchPackagesRequest } from '../../../../state/ui/configuration/packages/packageUiReducer';
import { cancelEditPackage, requestCreatePackage, requestUpdatePackage } from '../../../../state/ui/configuration/packages/packageUiActions';
import { currencySignForCode, DATE_FORMAT, convertToUTCDate } from '../packageUtil';
import { selectPackage } from '../../../../state/domain/packages/packageReducer';

// FIELDS
/**
 * The field for the package name.
 */
const NameField = injectIntl(({
  form, defaultValue, intl, disabled,
}) => (
  <Form.Item required label={<FormattedMessage id="packageName" />}>
    { form.getFieldDecorator('title', {
      initialValue: defaultValue,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
      ],
    })(
      <Input
        disabled={disabled}
        type="text"
        maxLength={80}
        allowClear
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

/**
 * The field for the marketing description
 */
const DescriptionField = injectIntl(({
  form, defaultValue, intl, disabled,
}) => (
  <Form.Item label={<FormattedMessage id="marketingDescription" />}>
    { form.getFieldDecorator('description', {
      initialValue: defaultValue,
    })(
      <Input
        disabled={disabled}
        type="text"
        maxLength={250}
        allowClear
        placeholder={disabled ? '' : intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

/**
 * The field for the display name label.  Allows clear, and max length set.
 * The rules are injected into the form using the form field decorator.
 */
const StartDateField = injectIntl(({
  form, creditPackage, intl, disabled,
}) => (
  <Form.Item required label={<FormattedMessage id="startDate" />}>
    { form.getFieldDecorator('packageStartDate', {
      initialValue: creditPackage ? moment.utc(creditPackage.packageStartDate) : undefined,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
      ],
    })(
      <DatePicker
        style={{ width: '300px' }}
        disabled={disabled}
        disabledDate={value => convertToUTCDate(value).isBefore(moment.utc()) || (form.getFieldValue('packageEndDate') ? value.isAfter(form.getFieldValue('packageEndDate').clone().endOf('day')) : false)}
        format={DATE_FORMAT}
        placeholder={intl.formatMessage({ id: 'selectOne' })}
        showToday={false}
      />,
    )}
  </Form.Item>
));


const EndDateField = injectIntl(({
  form, creditPackage, disabled, intl,
}) => (
  <Form.Item label={<FormattedMessage id="endDate" />}>
    { form.getFieldDecorator('packageEndDate', {
      initialValue: creditPackage && creditPackage.packageEndDate ? moment.utc(creditPackage.packageEndDate) : null,
    })(
      <DatePicker
        style={{ width: '300px' }}
        disabled={disabled}
        disabledDate={value => convertToUTCDate(value).isBefore(moment.utc()) || (form.getFieldValue('packageStartDate') ? value.isBefore(form.getFieldValue('packageStartDate').clone().startOf('day')) : false)}
        format={DATE_FORMAT}
        defaultPickerValue={form.getFieldValue('packageEndDate') || form.getFieldValue('packageStartDate')}
        placeholder={intl.formatMessage({ id: 'selectOne' })}
        showToday={false}
      />,
    )}
  </Form.Item>
));

const CurrencyField = injectIntl(({
  form, defaultValue, intl, disabled,
}) => (
  <Form.Item required label={<FormattedMessage id="currency" />}>
    { form.getFieldDecorator('currencyCode', {
      initialValue: defaultValue,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'price' }) },
      ],
    })(
      <Radio.Group disabled={disabled} buttonStyle="solid">
        <Radio.Button value="USD">$</Radio.Button>
        <Radio.Button value="GBP">£</Radio.Button>
        <Radio.Button value="EUR">€</Radio.Button>
      </Radio.Group>,
    )}
  </Form.Item>
));

const PriceField = injectIntl(({
  form, defaultValue, intl, disabled,
}) => (
  <Form.Item required label={<FormattedMessage id="price" />}>
    { form.getFieldDecorator('price', {
      initialValue: defaultValue,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
      ],
    })(
      <InputNumber
        disabled={disabled}
        style={{ width: '150px' }}
        formatter={value => `${currencySignForCode(form.getFieldValue('currencyCode'))} ${value}`}
        parser={value => value.replace(/€\s?|£\s?|\$\s?/g, '')}
        precision={2}
        min={0}
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

const MainTokensField = injectIntl(({
  form, defaultValue, intl, disabled,
}) => (
  <Form.Item required label={<FormattedMessage id="credits" />}>
    { form.getFieldDecorator('mainTokens', {
      initialValue: defaultValue,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
      ],
    })(
      <InputNumber
        disabled={disabled}
        precision={0}
        min={1}
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

const ExtraTokensField = injectIntl(({
  form, defaultValue, disabled, intl,
}) => (
  <Form.Item label={<FormattedMessage id="bonusCredits" />}>
    { form.getFieldDecorator('extraTokens', {
      initialValue: defaultValue,
    })(
      <InputNumber
        disabled={disabled}
        precision={0}
        min={0}
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

// MODAL AND FORM
const EditPackage = injectIntl(Form.create({ name: 'edit-package' })(({
  intl, form, visible, setVisible, creditPackage, onCreate, onUpdate,
  onCancel, isLoading, checklists, isSubmitting, validationBox, ...rest
}) => {
  const isCreating = !creditPackage;
  const titleId = isCreating ? 'create' : 'edit';
  const buttonId = isCreating ? 'create' : 'save';

  const canEditAll = isCreating || !creditPackage.active || moment.utc().isBefore(moment.utc(creditPackage.packageStartDate));
  const canEditEndDate = canEditAll || !creditPackage.packageEndDate || moment.utc().isBefore(moment.utc(creditPackage.packageEndDate));

  const { resetFields } = form;

  // Reset fields when it becomes visible.
  useEffect(() => {
    if (visible) {
      resetFields();
    }
  }, [resetFields, visible]);

  // On form submit.
  const submitForm = useCallback(() => {
    if (!canEditEndDate) { onCancel(); return; }
    form.validateFields();
    form.validateFields((err, values) => {
      if (!err) {
        const newPackage = creditPackage
          ? {
            ...values,
            code: creditPackage.code,
            active: creditPackage.active,
            registration: creditPackage.registration,
            packageStartDate: convertToUTCDate(values.packageStartDate).startOf('day').toISOString(),
            packageEndDate: values.packageEndDate ? convertToUTCDate(values.packageEndDate).endOf('day').toISOString() : null,
          }
          : {
            ...values,
            packageStartDate: convertToUTCDate(values.packageStartDate).startOf('day').toISOString(),
            packageEndDate: values.packageEndDate ? convertToUTCDate(values.packageEndDate).endOf('day').toISOString() : null,
            active: false,
            registration: false,
          };
        const submitFunction = isCreating ? onCreate : onUpdate;
        submitFunction(newPackage, intl);
      }
    });
  }, [form, isCreating, onCreate, onUpdate, intl, canEditEndDate, creditPackage, onCancel]);

  return (
    <Modal
      title={<FormattedMessage id={titleId} />}
      okText={<FormattedMessage id={buttonId} />}
      onOk={submitForm}
      visible={visible}
      onCancel={onCancel}
      confirmLoading={isSubmitting}
      {...rest}
    >
      <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); submitForm(); }}>
        <ViaValidationAlert validationError={validationBox} />
        <NameField form={form} defaultValue={creditPackage ? creditPackage.title : null} disabled={!canEditAll} />
        <DescriptionField form={form} defaultValue={creditPackage ? creditPackage.description : null} disabled={!canEditAll} />
        <CurrencyField form={form} defaultValue={creditPackage ? creditPackage.currencyCode : null} disabled={!canEditAll} />
        <PriceField form={form} defaultValue={creditPackage ? creditPackage.price : null} disabled={!canEditAll} />
        <MainTokensField form={form} defaultValue={creditPackage ? creditPackage.mainTokens : null} disabled={!canEditAll} />
        <ExtraTokensField form={form} defaultValue={creditPackage ? creditPackage.extraTokens : null} disabled={!canEditAll} />
        <StartDateField form={form} creditPackage={creditPackage} disabled={!canEditAll} />
        <EndDateField form={form} creditPackage={creditPackage} disabled={!canEditEndDate} />
      </Form>
    </Modal>
  );
}));

// Validate the prop type parameters.
EditPackage.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  validationBox: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
};

EditPackage.defaultProps = {
  isLoading: false,
  validationBox: null,
};

const mapStateToProps = (state) => {
  const editState = selectEditState(state);
  const submitRequest = selectSubmitPackageRequest(state);
  const pageDataRequest = selectFetchPackagesRequest(state);

  return {
    visible: editState.isEditing,
    creditPackage: editState.code ? selectPackage(state, editState.code) : null,
    isSubmitting: submitRequest ? submitRequest.isLoading : false,
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    validationBox: submitRequest ? submitRequest.error : undefined,
  };
};

const mapDispatchToProps = dispatch => ({
  onCreate: newPackage => dispatch(requestCreatePackage(newPackage)),
  onUpdate: newPackage => dispatch(requestUpdatePackage(newPackage)),
  onCancel: () => dispatch(cancelEditPackage()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditPackage);
